export const meta = {
  org: "HARC — Houston Advanced Research Center",
  title: "Website Architecture Recommendation",
  subtitle:
    "Phase 1 deliverable: current-site audit, CMS viability assessment, scored architecture comparison, and recommended path — prepared for approval before any design or development begins.",
  date: "August 2026",
  status: "Draft for HARC approval — development is stopped pending sign-off",
};

export const sections = [
  {
    id: "executive-summary",
    num: "01",
    title: "Executive Summary & Recommendation",
    blocks: [
      {
        lead: "HARC does not need a new platform. The audit shows a healthy, actively maintained WordPress foundation with the right integrations already in place. The redesign should be delivered by retaining and modernizing the existing WordPress CMS — not by replatforming.",
      },
      {
        p: "The current site (harcresearch.org) runs WordPress on WP Engine behind Cloudflare, with Rank Math for SEO, Gravity Forms for forms, Mailchimp for newsletters, and Qgiv for donations. The limiting factor is not the CMS — it is a bespoke classic theme, content organized around internal research disciplines rather than audience challenges, and the absence of a structured Impact Story model and a filterable resource library. All of these can be solved inside WordPress at a fraction of the cost and risk of a rebuild.",
      },
      {
        p: "Recommended: Option A — Retain and Modernize. Rebuild the theme and information architecture on the existing WordPress backend, introduce structured content models (Impact Stories, unified Insights & Resources), keep the working integrations, and migrate only strategic, high-value content. Scored against the agreed criteria, Option A totals 29/35 versus 20/35 for a new React/FastAPI/MongoDB build and 21/35 for a headless hybrid.",
      },
      {
        callout: {
          tone: "green",
          label: "Recommendation",
          text: "Approve Option A (Retain + Modernize WordPress). Hold Option C (headless) as the documented fallback if administrative access reveals the backend cannot be adequately maintained. Do not proceed with Option B; it adds cost, migration risk, and an ongoing developer dependency without a compensating benefit for HARC's actual requirements.",
        },
      },
    ],
  },
  {
    id: "current-site-assessment",
    num: "02",
    title: "Current-Site Assessment",
    blocks: [
      {
        p: "The following was verified by direct inspection of the public website on 26 August 2026 (HTTP headers, sitemaps, REST API, page source).",
      },
      {
        table: {
          head: ["Layer", "Finding", "Evidence"],
          rows: [
            ["CMS", "WordPress, custom classic theme named “HARC”", "wp-content/themes/HARC, wp-json headers"],
            ["Hosting / CDN", "WP Engine (managed WordPress) behind Cloudflare", "x-powered-by: WP Engine; server: cloudflare"],
            ["SEO", "Rank Math SEO; Google Site Kit; Google Tag Manager (GTM-P9RD6M4)", "Sitemap generator tag; page source"],
            ["Forms", "Gravity Forms on the Contact page", "Page source signatures"],
            ["Newsletter", "Mailchimp (list-manage embed + mailchi.mp archive)", "/newsletter-sign-up/ page source"],
            ["Donations", "Qgiv — secure.qgiv.com/for/houstonadvancedresearchcenter", "Donate page iframe"],
            ["Podcast", "“Energy Crossroads” distributed via Spotify/Anchor", "Episode page embeds"],
            ["Front-end", "jQuery + Slick carousel, FontAwesome; no JS framework", "Page source"],
          ],
        },
      },
      { h: "Content inventory (verified via sitemaps and REST API)" },
      {
        table: {
          head: ["Content", "Count", "Activity window", "Notes"],
          rows: [
            ["Pages", "36", "2020 → Aug 2026", "Actively edited"],
            ["Research (custom post type)", "70", "2020 → Aug 2026", "The de-facto project/case library"],
            ["People (custom post type)", "48", "2020 → Aug 2026", "Team, board, fellows"],
            ["News (custom post type)", "244", "Bulk of edits 2023 → Jul 2026", "Flat single stream; no type or topic facets"],
            ["Events (custom post type)", "17", "Stale — untouched since Jan 2025", "Candidate for retirement"],
            ["Blog posts (native)", "1", "—", "Unused default “Hello world”; blog concept folded into News"],
          ],
        },
      },
      {
        p: "Key structural observation: the top-level navigation is organized around internal disciplines (/air, /energy, /water, /resilience, /geospatialsolutions) — exactly the “multiple disconnected service areas” the HARC brief asks to replace with one unified, solutions-led brand. The custom post types are not exposed to the WordPress block editor (absent from the REST types index), confirming a classic PHP theme where layout changes require a developer.",
      },
    ],
  },
  {
    id: "cms-viability",
    num: "03",
    title: "Existing CMS/Backend Viability",
    blocks: [
      {
        p: "Assessment question: can the existing backend support the required redesign, editorial model, search/filter functionality, forms, and staff workflow? Verdict: yes, with a theme rebuild and content-model work — no replatforming required.",
      },
      {
        list: [
          "Secure and maintained: WP Engine provides managed core updates, daily backups, and a Web Application Firewall via Cloudflare. Nothing in the stack is end-of-life.",
          "Editorial capability: WordPress with custom post types, custom fields, and reusable block patterns fully supports code-free publishing for nontechnical staff. This is its core strength — the current theme simply doesn't take advantage of it.",
          "Search and filtering: the required keyword + topic + content-type filtering is standard WordPress territory (WP_Query, taxonomy archives, or a proven add-on such as FacetWP/SearchWP).",
          "Forms and routing: Gravity Forms already installed; it supports conditional notifications (routing submissions to the right HARC contact), spam protection, and CRM-ready export/webhooks.",
          "Integrations: Mailchimp and Qgiv are live and working — both should be preserved, per the brief's own guidance.",
          "What is genuinely missing: a block-editable theme, an Impact Story content model, a unified insights taxonomy, and faceted filtering. All are theme/content-model work, not platform work.",
        ],
      },
      {
        callout: {
          tone: "amber",
          label: "Unverified",
          text: "CMS administrative access, plugin license status (Gravity Forms, any premium plugins), WP Engine plan limits, and editor role structure have not yet been inspected from inside the dashboard. This assessment is based on the public footprint and must be confirmed during a short access-verification step before final approval.",
        },
      },
    ],
  },
  {
    id: "keep-modify-replace",
    num: "04",
    title: "Keep / Modify / Replace Analysis",
    blocks: [
      {
        table: {
          head: ["Component", "Decision", "Rationale"],
          rows: [
            ["WordPress core + WP Engine hosting + Cloudflare", "KEEP", "Secure, maintained, cost-effective; no requirement it fails to meet"],
            ["Rank Math SEO + Google Site Kit / Tag Manager", "KEEP", "Working SEO/analytics foundation; preserves meta data through redesign"],
            ["Gravity Forms", "KEEP + CONFIGURE", "Add the Start a Conversation form with conditional routing to the right HARC contact"],
            ["Mailchimp newsletter", "KEEP", "Existing audience and signup flow; embed in new templates"],
            ["Qgiv donations", "KEEP", "Secure, nonprofit-appropriate; no business case for custom payments"],
            ["Custom “HARC” classic theme", "REPLACE (theme only)", "Requires code for layout changes; not block-editable; discipline-siloed templates"],
            ["Information architecture & navigation", "REPLACE", "Move from discipline silos to the approved journey: About / Approach / Impact / Insights & Resources / Donate / Start a Conversation"],
            ["Content models", "MODIFY + EXTEND", "Add Impact Story CPT with Challenge→Approach→Solution→Impact fields; unify News into a typed, topic-tagged Insights library"],
            ["Events post type", "RETIRE", "Stale since Jan 2025; redirect URLs, archive data"],
            ["~10 years of legacy content", "TRIAGE", "Migrate strategic/high-traffic items; consolidate, archive, redirect, or retire the rest (see Migration Strategy)"],
          ],
        },
      },
    ],
  },
  {
    id: "options",
    num: "05",
    title: "The Three Architecture Options",
    blocks: [
      {
        h: "Option A — Retain and Modernize Existing CMS",
      },
      {
        p: "Keep WordPress on WP Engine. Rebuild the theme as a modern, block-editable design system; restructure navigation and content models; add faceted search/filter; configure forms and integrations; migrate selected content. Editors gain code-free publishing through blocks and patterns.",
      },
      { h: "Option B — New Modern Architecture (React + FastAPI + MongoDB)" },
      {
        p: "A fully custom new build. Technically excellent, but every CMS convenience HARC relies on — editorial UI, roles, revisions, media library, SEO tooling, forms, spam control, previews — would have to be rebuilt or bought, and the result would require ongoing developer support. This contradicts HARC's budget-sensitivity principles and is not justified by any unmet requirement.",
      },
      { h: "Option C — Hybrid / Headless" },
      {
        p: "Keep WordPress as a headless content API and rebuild the public front end (e.g., a React static/hybrid front). Improves front-end performance ceiling, but doubles the stack, complicates staff preview/publishing, adds hosting complexity, and creates a developer dependency for routine changes. It solves a performance problem HARC does not have — the current site is already served via WP Engine page cache plus Cloudflare.",
      },
    ],
  },
  {
    id: "scoring",
    num: "06",
    title: "Scored Evaluation",
    blocks: [
      {
        p: "Scale 1–5; 5 is most favorable. For cost, risk, and maintenance burden, a higher score means lower cost, lower risk, or lower burden. Scores marked * rest on unverified factors and are deliberately not optimistic.",
      },
      {
        table: {
          head: ["Criterion", "A — Retain + Modernize", "B — New Build", "C — Hybrid / Headless"],
          highlight: 1,
          rows: [
            ["Initial cost", "5 — theme + content-model work only", "2 — full custom build + CMS tooling", "2 — two stacks to build and host"],
            ["Migration risk", "4 — content stays in place; URL-preserving", "2 — full content + media migration", "3 — content stays, front end rebuilt"],
            ["Staff usability", "4 — native, familiar block editing*", "2 — custom admin to build and learn", "3 — editing in WP, preview complexity"],
            ["Maintenance burden", "4 — managed host, no developer retainer*", "2 — developer required indefinitely", "2 — developer required indefinitely"],
            ["SEO risk", "5 — same platform, URLs preservable", "3 — full redirect map, re-indexing", "3 — full redirect map, re-indexing"],
            ["Performance", "3 — good (WP cache + Cloudflare CDN)", "5 — highest ceiling", "5 — highest ceiling"],
            ["Long-term flexibility", "4 — vast ecosystem, low lock-in", "4 — fully custom, fully owned", "3 — coupled to a custom front end"],
            ["TOTAL", "29 / 35", "20 / 35", "21 / 35"],
          ],
        },
      },
      {
        callout: {
          tone: "green",
          label: "Why Option A wins",
          text: "Every gap between the current site and the required experience is a theme, content-model, or configuration gap — none is a platform gap. Option A is the only option that satisfies the brief's own budget principles (reuse, simplicity, minimal subscriptions, low technical debt) while delivering 100% of the required functionality. Option B's higher performance and flexibility scores do not compensate for triple the cost and a permanent developer dependency at a nonprofit.",
        },
      },
    ],
  },
  {
    id: "recommended-architecture",
    num: "07",
    title: "Recommended Architecture (Option A)",
    blocks: {
      list: [
        "Platform: WordPress (existing) on WP Engine (existing) behind Cloudflare (existing) — no hosting or platform change.",
        "Theme: replace the classic “HARC” theme with a modern block-enabled theme implementing the approved design system; all page sections become reusable, editor-composable blocks/patterns.",
        "Content modeling: custom post types + custom fields (e.g., ACF or native block bindings) for Impact Stories, Insights items, People, Careers, and flexible Pages — editors never touch code or layout.",
        "Search/filter: faceted library via taxonomy-driven queries (FacetWP, SearchWP, or custom WP_Query — selected in prototype against real content volume).",
        "Forms: Gravity Forms — Start a Conversation form with conditional notification routing; entries exportable/webhook-ready for a future CRM, without building one.",
        "Newsletter: existing Mailchimp — embedded signup in footer, homepage section, and dedicated page.",
        "Donations: existing Qgiv — embedded/linked from the new Donate page.",
        "SEO: Rank Math retained; per-item SEO title, meta description, social image fields on every content type.",
        "Analytics: existing Google Site Kit / GTM, extended with conversion events (form submit, newsletter signup, donate click).",
      ],
    },
  },
  {
    id: "sitemap",
    num: "08",
    title: "Proposed Sitemap",
    blocks: [
      {
        p: "Structured around the approved visitor journey: Complex challenge → HARC solves challenges like mine → Understand the approach → See impact → Start a conversation.",
      },
      {
        table: {
          head: ["Section", "Pages", "Notes"],
          rows: [
            ["Home", "/", "Solutions-led; hero, How HARC Creates Impact, story previews, approach teaser, latest insights, final CTA + newsletter"],
            ["About HARC", "/about/ + Mission & History, Team, Board / Advisory Council / Fellows, Living Lab, Careers", "People content consolidated under About"],
            ["Approach", "/approach/", "Understand → Connect → Solve → Impact framework; partner-sector pathways"],
            ["Impact", "/impact/ + individual Impact Stories", "Story index with problem-led cards; the proof layer"],
            ["Insights & Resources", "/insights/ + filtered library; type hubs for Articles, Reports, Podcasts, News", "One searchable library replaces the flat news stream"],
            ["Donate", "/donate/", "Existing Qgiv embed retained"],
            ["Start a Conversation", "/start-a-conversation/", "Primary conversion CTA in navigation, visually differentiated; replaces /contact/ (301)"],
            ["Utility", "Search results, Privacy Policy, Terms of Use, Accessibility statement, Newsletter signup", "Footer-linked"],
            ["Retired", "/events/ and discipline silos /air /energy /water /resilience /geospatialsolutions", "301 to nearest new destination; content folded into topics and Impact Stories"],
          ],
        },
      },
    ],
  },
  {
    id: "content-model",
    num: "09",
    title: "Proposed Content Model",
    blocks: [
      {
        p: "All types share a common editorial spine so staff learn one pattern: title, summary, block-based body, featured image, topics, publish date, featured flag, related content, related people, SEO title, meta description, social image, URL slug. No code, no manual layout.",
      },
      {
        table: {
          head: ["Content type", "Distinguishing fields", "Source"],
          rows: [
            ["Impact Story", "Challenge, HARC Approach, Solution, Impact (structured sub-sections); partner sector; outcomes/photos", "New — curated from top Research items"],
            ["Article / Blog", "Byline, related people", "Migrated News subset"],
            ["Report", "Downloadable PDF attachment, publication date", "Migrated from News/Research"],
            ["Podcast Episode", "Audio embed (existing Spotify/Anchor), transcript for accessibility", "Migrated from News"],
            ["News", "Dateline, optional external link", "Recent News items"],
            ["Resource", "Type tag (guide, tool, dataset, video)", "Consolidated from Research/News"],
            ["Team Member", "Role, bio, photo, related content", "Existing People CPT"],
            ["Board / Advisory / Fellows", "Group affiliation, bio, photo", "Existing People CPT"],
            ["Career", "Department, location, application link, closing date", "New lightweight CPT"],
            ["Page", "Flexible block sections", "Existing Pages"],
          ],
        },
      },
    ],
  },
  {
    id: "migration",
    num: "10",
    title: "Migration Strategy",
    blocks: [
      {
        p: "Principle: favor strategic, high-value content over complete historical migration. Because Option A keeps the database in place, “migration” is primarily reclassification and curation — not export/import — which is the single largest cost and risk saver of the recommended architecture.",
      },
      {
        table: {
          head: ["Disposition", "Applies to", "Action"],
          rows: [
            ["Migrate", "Top Research projects (by traffic + strategic value, verified in analytics); current People; News/Reports/Podcasts from roughly the last 3 years; evergreen high-traffic items", "Retag to new topics; convert standout projects into Impact Stories"],
            ["Consolidate", "Discipline silo pages; overlapping news announcements of the same initiative; near-duplicate tags/categories", "Merge into topic hubs and single canonical items"],
            ["Archive", "Older low-traffic news (>3 years) with historical value", "Export to a read-only archive; remove from indexation"],
            ["Redirect", "Every retired or merged URL — including /contact/, /events/*, discipline pages", "301 map to nearest new destination; preserve link equity"],
            ["Retire", "Events post type (stale since Jan 2025), default placeholder post, obsolete taxonomy terms", "Remove after redirect; document in content register"],
          ],
        },
      },
      {
        callout: {
          tone: "amber",
          label: "Unverified",
          text: "Which legacy items generate meaningful organic traffic and which have strategic value requires analytics access (Google Analytics via Site Kit) plus a HARC editorial review session. The 3-year news window is a working assumption to be tuned with that data.",
        },
      },
    ],
  },
  {
    id: "taxonomy",
    num: "11",
    title: "Resource Taxonomy Strategy",
    blocks: [
      {
        p: "The current structure has no usable public taxonomy for insights: News is a single flat stream of 244 items, and the five discipline silos duplicate what should be topics. Recommendation: replace with one compact Topics vocabulary (working draft below, to be validated against the content audit) plus a fixed Content Type facet.",
      },
      {
        list: [
          "Topics (draft): Air Quality; Climate & Resilience; Energy; Water; Infrastructure & the Built Environment; Geospatial & Data; Communities & Policy. Roughly 7–9 terms — deliberately small so filtering stays meaningful and tagging stays consistent.",
          "Content types (fixed): Article, Report, Podcast, News, Resource.",
          "Deduplication: map the legacy /air /energy /water /resilience /geospatialsolutions silos onto Topics; merge duplicate and near-duplicate legacy terms; retire obsolete types (Events).",
          "Gap filled: partner-sector tags (Government, Business, Nonprofits, Philanthropy, Communities, Academia) on Impact Stories, powering the Approach page's sector pathways.",
        ],
      },
    ],
  },
  {
    id: "search-filter",
    num: "12",
    title: "Search & Filter Approach",
    blocks: [
      {
        list: [
          "One Insights & Resources library with keyword search, Topic filter, Content-Type filter, newest-first sorting, featured pinning, and related-content modules on detail pages.",
          "Global site search in the header spans pages, Impact Stories, and the full insights corpus, with grouped results and an empty state that routes visitors back into the library.",
          "Implementation: taxonomy-driven WordPress queries with AJAX faceting (FacetWP or SearchWP selected at prototype against the real ~300-item corpus — at this scale either is comfortably fast; no external search service or subscription is warranted).",
          "Search relevance: titles and summaries weighted above body text; reports' PDF metadata indexed.",
          "Editorial control: “Featured” flag drives homepage and landing-page slots; related content defaults to shared topics with manual override.",
        ],
      },
    ],
  },
  {
    id: "impact-stories",
    num: "13",
    title: "Impact Story Architecture",
    blocks: [
      {
        p: "Impact Story is a dedicated structured content type with four required narrative fields — The Challenge, HARC's Approach, The Solution, The Impact — plus featured image, photo gallery, partner-sector tag, topics, related people, related insights, and full SEO fields. Editors fill fields; the template renders the consistent story framework automatically.",
      },
      {
        list: [
          "Reusable: story cards feed the homepage previews, the /impact/ index, and sector pathways on the Approach page from a single source.",
          "Searchable: stories share the Topics vocabulary, so they surface in the Insights library and site search where appropriate.",
          "Placeholder-safe: stories can publish with [HARC CONTENT REQUIRED] blocks during the prototype without blocking templates, and can stay in draft until verified outcomes exist.",
          "Honest-content guardrail: no outcome, statistic, partnership, or project claim is ever invented — fields stay placeholdered until HARC supplies verified content.",
        ],
      },
    ],
  },
  {
    id: "forms",
    num: "14",
    title: "Forms & Lead Generation",
    blocks: [
      {
        list: [
          "Tooling: Gravity Forms (already installed and licensed* — confirm license tier during access verification).",
          "Start a Conversation captures exactly: Name; Organization; Email; Phone; “What challenge are you trying to solve?”; “How did you hear about HARC?”.",
          "Routing: conditional notifications send each submission to the appropriate HARC contact based on challenge type; the lead-monitoring workflow from the HARC brief is handled via entry assignment/notes plus email notifications — no custom CRM is built.",
          "CRM-readiness: entries are exportable and webhook-capable, so a future CRM (HubSpot, Salesforce, Bloomerang) can subscribe later with zero re-platforming.",
          "Pop-up CTA: the brief's contact pop-up is implemented as a dismissible, accessible dialog linking to the form (frequency-capped, keyboard- and screen-reader-operable, suppressed after submission).",
          "Protection: existing spam controls plus Cloudflare; privacy notice and consent captured at the form.",
        ],
      },
      {
        callout: {
          tone: "amber",
          label: "Unverified",
          text: "Who owns lead follow-up inside HARC and whether any CRM already exists are open questions for HARC. Form routing rules will be configured once the responsible contacts are named.",
        },
      },
    ],
  },
  {
    id: "newsletter-donation",
    num: "15",
    title: "Newsletter & Donation Integration",
    blocks: [
      {
        table: {
          head: ["Integration", "Verified provider", "Plan"],
          rows: [
            ["Newsletter", "Mailchimp (list-manage embed; mailchi.mp campaign archive)", "Keep. Embed the existing signup in the homepage “Stay Connected” section, footer, and /newsletter-sign-up/. No new email platform is introduced."],
            ["Donations", "Qgiv (secure.qgiv.com/for/houstonadvancedresearchcenter)", "Keep. The new Donate page keeps the secure Qgiv embed; the brief's approved Donate copy wraps it. No custom payment processing is built."],
          ],
        },
      },
    ],
  },
  {
    id: "seo-domain",
    num: "16",
    title: "SEO & Domain Migration Strategy",
    blocks: [
      {
        list: [
          "Preserve equity: keep existing URLs wherever the page survives; ship a complete 301 map for everything merged, retired, or renamed; carry over Rank Math titles/metas; maintain XML sitemaps and Search Console verification throughout.",
          "Content SEO: the new problem-led headlines, structured Impact Stories, and topic hubs improve on the current discipline-silo structure; podcast episodes gain transcripts (accessibility + indexable text).",
          "Domain transition (harcresearch.org → harcsolutions.org): prepared for, not performed, during this engagement. Preparation means: the new theme, templates, Mailchimp/Qgiv settings, and analytics use no hardcoded domain; a documented runbook (site-wide 301s, Search Console Change of Address, both domains retained indefinitely, update Mailchimp/Qgiv/social profiles) is delivered for execution at HARC's chosen time.",
          "Ownership: the HARC brief indicates harcsolutions.org is available/secured and harcresearch.org will be retained and rerouted — registration status must be verified before launch planning.",
        ],
      },
    ],
  },
  {
    id: "accessibility",
    num: "17",
    title: "Accessibility Considerations",
    blocks: [
      {
        list: [
          "Target WCAG 2.2 AA from the first template, not as a retrofit: color-contrast-checked palette, visible focus states, skip links, logical heading order, keyboard-operable navigation, dialogs, and filters.",
          "Editorial guardrails: required alt text on images, captions/transcripts for podcast and video embeds, and block patterns that cannot produce broken heading hierarchies.",
          "Forms: labeled fields, error identification, and confirmation messaging (Gravity Forms supports AA-grade output when configured).",
          "Motion: subtle by design per the UI direction; honor reduced-motion preferences.",
          "Verification: automated scanning plus keyboard and screen-reader passes in Phase 3 QA; an Accessibility statement page in the footer.",
        ],
      },
    ],
  },
  {
    id: "security-maintenance",
    num: "18",
    title: "Security & Maintenance Considerations",
    blocks: [
      {
        list: [
          "Managed layer retained: WP Engine core updates, daily backups, staging environment; Cloudflare WAF/CDN already in front.",
          "Housekeeping during rebuild: plugin audit (remove anything unused), enforce least-privilege editor roles, require 2FA for administrators, confirm Gravity Forms and all premium plugins are licensed and current.",
          "Maintainability: the new theme uses standard WordPress block/field APIs — any competent WordPress agency can maintain it; no proprietary workflow, no custom framework, no developer retainer required for publishing.",
          "Ongoing cost stays flat: existing WP Engine + Cloudflare + Mailchimp + Qgiv subscriptions; the only prospective new line item is an optional search/filter plugin license (~$100–200/yr) if the premium route is chosen.",
        ],
      },
    ],
  },
  {
    id: "risks",
    num: "19",
    title: "Major Risks & Unknowns",
    blocks: [
      {
        table: {
          head: ["Item", "Status", "Mitigation"],
          rows: [
            ["CMS admin access, plugin licenses, hosting plan limits", "UNVERIFIED", "Access-verification step before final approval; fallback to Option C if the backend proves unmaintainable"],
            ["Analytics: which legacy content carries traffic", "UNVERIFIED", "Analytics review drives the migrate/archive triage"],
            ["Lead routing ownership; existence of a CRM", "UNVERIFIED", "HARC to name responsible contacts; form ships with email routing regardless"],
            ["harcsolutions.org ownership/registration", "UNVERIFIED", "Confirm registration; domain move deferred regardless"],
            ["Verified Impact Story content availability", "KNOWN GAP", "Placeholder architecture ([HARC CONTENT REQUIRED]) unblocks the prototype"],
            ["Editorial capacity for tagging ~300 items during triage", "RISK", "Phased triage; bulk-edit tooling; traffic data narrows the set"],
            ["Scope creep into a custom CRM or donation build", "RISK", "Explicitly out of scope per brief; change-control at each phase gate"],
          ],
        },
      },
    ],
  },
  {
    id: "cost-escalators",
    num: "20",
    title: "Items Likely to Materially Increase Cost",
    blocks: [
      {
        list: [
          "Migrating all ~10 years of legacy content instead of triaging to strategic items (the single largest avoidable cost).",
          "A custom-built search service or external search subscription where proven WordPress faceting suffices.",
          "Building a custom CRM or custom payment processing (both explicitly discouraged by the brief).",
          "Performing the domain migration concurrently with the redesign rather than as a later, separately planned step.",
          "Bespoke per-page art direction instead of a reusable block/pattern system.",
          "Retrofitting accessibility after build instead of designing to WCAG 2.2 AA from the start.",
        ],
      },
    ],
  },
  {
    id: "prototype-scope",
    num: "21",
    title: "Recommended Prototype Scope (Phase 2, post-approval)",
    blocks: [
      {
        p: "Exactly six components, built as a staging copy on the existing WP Engine instance with the new theme — so approved prototype work carries directly into production with zero rework. Each component below lists its wireframe (top to bottom) and its acceptance criteria. Verified HARC copy is used wherever available, with explicit [HARC CONTENT REQUIRED] placeholders elsewhere; no outcomes, statistics, partnerships, or project claims are invented.",
      },
      { h: "1 — Global header & navigation" },
      {
        list: [
          "Sticky bar: HARC logo left; primary nav — About HARC, Approach, Impact, Insights & Resources, Donate; “Start a Conversation” rendered as a filled, high-contrast button, clearly differentiated from text links.",
          "On scroll the bar condenses and persists; a skip-to-content link is first in tab order.",
          "Mobile: hamburger opens a full-height menu in the same order, with the CTA pinned at the bottom; fully keyboard- and screen-reader-operable.",
          "Acceptance: identical header on every page; CTA visibly distinct at all breakpoints; passes WCAG 2.2 AA focus and contrast checks.",
        ],
      },
      { h: "2 — Homepage (six approved sections, in journey order)" },
      {
        list: [
          "Hero: full-width environmental/community image; “Where Science Creates Solutions” with the approved subline; “Talk to HARC →”.",
          "How HARC Creates Impact: the approved three-paragraph positioning copy; “How We Work →”.",
          "What HARC Makes Possible: three Impact Story preview cards (strong image, problem-led headline, 1–2 sentence teaser, “Read the Story →”); placeholder cards until verified stories exist.",
          "See How We Work: approved approach copy; “Explore Our Approach →”.",
          "Insights & Resources: newest items first across Articles, Podcasts, and Reports; “Search for a specific Topic →” deep-links into the pre-filtered library.",
          "Final CTA (“Have a Complex Challenge? Let’s Solve It Together.” + “Talk to HARC →”) beside the “Stay Connected” Mailchimp signup band.",
          "Pop-up: the brief’s contact dialog — dismissible, frequency-capped, keyboard- and screen-reader-operable, suppressed after a visitor submits the form.",
          "Acceptance: the page reads challenge → solution → proof → conversation; every CTA resolves; zero invented claims.",
        ],
      },
      { h: "3 — One representative Impact Story" },
      {
        list: [
          "Hero image with problem-led headline; meta strip showing partner sector, topics, and date.",
          "Four structured narrative blocks in fixed order — The Challenge → HARC’s Approach → The Solution → The Impact — followed by photo gallery, related people, related insights, and a closing “Talk to HARC →” banner.",
          "Built entirely from the Impact Story content model so HARC staff see the exact editorial fields they will use; [HARC CONTENT REQUIRED] placeholders wherever verified outcomes are not yet available.",
          "Acceptance: the template renders wholly from fields with no code; the placeholder story can be swapped for a verified one with no design change.",
        ],
      },
      { h: "4 — Insights & Resources landing with search/filter" },
      {
        list: [
          "Intro (“Ideas, Research, and Perspectives That Move Us Forward” + approved copy) with a prominent keyword search field.",
          "Filter row: Topic select, Content-Type select (Article, Report, Podcast, News, Resource), newest-first sort; featured items pinned; active filters reflected in the URL so results are shareable.",
          "Below the library: Podcasts (latest 3 + “view all”), Articles, Reports, and News sections, each linking into the pre-filtered library.",
          "Result cards: type badge, title, 1–2 line summary, date, topics; empty state with a clear-filters action.",
          "Acceptance: keyword + topic + type filtering runs against a real imported sample (~30–50 items); newest-first is the default; the featured flag drives homepage and landing slots.",
        ],
      },
      { h: "5 — Start a Conversation page" },
      {
        list: [
          "Lead-generation form with exactly: Name; Organization; Email; Phone; “What challenge are you trying to solve?”; “How did you hear about HARC?”.",
          "Sidebar with office address, phone, and general email, plus a routing note (“your enquiry goes directly to the right HARC team”).",
          "Confirmation state sets follow-up expectations; Gravity Forms conditional notifications route each submission to the appropriate HARC contact; entries remain exportable/webhook-ready for a future CRM.",
          "Acceptance: a test submission arrives at the designated inbox; the form is fully keyboard- and screen-reader-operable with clear error messaging; spam protection is active.",
        ],
      },
      { h: "6 — Global footer" },
      {
        list: [
          "Top band: “Have a Complex Challenge? Let’s find the right solution together.” + “Let’s Solve Something Together →”.",
          "Middle: HARC logo with the brand message (“Where Science Creates Solutions…”); link columns Explore / Connect / Resources per the approved map; social links; “Stay Connected” Mailchimp signup.",
          "Bottom bar: © 2026 HARC · Privacy Policy · Terms of Use · Accessibility · address.",
          "Acceptance: identical footer on every page; newsletter signup verified against the existing Mailchimp list.",
        ],
      },
      { h: "Prototype logistics & review gate" },
      {
        list: [
          "Content: verified HARC copy from the approved brief; ~30–50 real insights items imported so filtering can be tested honestly; placeholder policy [HARC CONTENT REQUIRED] throughout.",
          "Indicative timeline: 4–6 weeks including one consolidated HARC review cycle (confirmed once resourcing is set).",
          "STOP: HARC signs off on all six components before any Phase 3 work begins.",
        ],
      },
    ],
  },
  {
    id: "approval",
    num: "22",
    title: "Approval & Next Steps",
    blocks: [
      {
        numlist: [
          "HARC reviews and approves this Architecture Recommendation (or requests changes).",
          "Access verification (≈1 week): CMS admin, hosting, analytics, plugin licenses, Mailchimp/Qgiv configuration, lead-routing ownership — converting every UNVERIFIED flag in this document into fact.",
          "Content triage workshop: analytics-led decisions on migrate / consolidate / archive / redirect / retire, and validation of the Topics vocabulary.",
          "Phase 2 prototype (six components above) → STOP for review.",
          "Phase 3 full implementation: remaining templates, curated migration, search/filter, forms, integrations, redirects, SEO foundations, WCAG 2.2 AA QA, staff training, launch preparation — with the domain transition runbook held for HARC's chosen date.",
        ],
      },
      {
        callout: {
          tone: "green",
          label: "Decision requested",
          text: "Approve Option A — Retain and Modernize the existing WordPress platform — and authorize the access-verification step so Phase 2 prototype planning can begin.",
        },
      },
    ],
  },
];
